import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAnimeDetail } from '@/hooks/useAnimeQueries';
import { useLibrary } from '@/hooks/useLibrary';
import { formatScore } from '@/lib/format';
import type { AnimeSummary } from '@/types/anime';

interface HeroSpotlightProps {
	anime: AnimeSummary | undefined;
	isLoading?: boolean;
}

/**
 * Banner nổi bật. Bản gốc hardcode "Jujutsu Kaisen Season 2"; ở đây lấy tựa
 * hoạt hình được xem nhiều nhất từ API, và gọi thêm endpoint chi tiết để có
 * mô tả thật (danh sách không trả về `content`).
 */
export function HeroSpotlight({ anime, isLoading }: HeroSpotlightProps) {
	const { isSaved, toggle } = useLibrary();
	const detailQuery = useAnimeDetail(anime?.slug);

	if (isLoading || !anime) {
		return <Skeleton className="min-h-[360px] w-full rounded-hero" />;
	}

	const synopsis =
		detailQuery.data?.synopsis ||
		'Đang tải giới thiệu nội dung từ kho phim...';
	const inWatchlist = isSaved('watchlist', anime.slug);

	const metaItems = [
		anime.quality,
		anime.year ? String(anime.year) : '',
		anime.episodeCurrent,
		anime.score !== null ? `★ ${formatScore(anime.score)}` : '',
		anime.lang,
	].filter(Boolean);

	return (
		<section
			className="animate-fade-in relative isolate flex min-h-[410px] items-end overflow-hidden rounded-hero border border-white/[0.06] bg-surface shadow-neko min-[700px]:min-h-[360px] min-[700px]:items-center"
			aria-label="Anime nổi bật"
		>
			<img
				src={anime.thumbUrl}
				alt=""
				aria-hidden="true"
				className="absolute inset-0 -z-20 size-full object-cover"
			/>
			<span
				className="absolute inset-0 -z-10 bg-linear-to-r from-[#141222] from-14% via-[#151426bb] via-53% to-transparent"
				aria-hidden="true"
			/>
			<span
				className="absolute inset-x-0 bottom-0 -z-10 h-25 bg-linear-to-b from-transparent to-bg"
				aria-hidden="true"
			/>

			<div className="relative z-10 max-w-[500px] p-7 [text-shadow:0_2px_20px_rgb(0_0_0/0.32)] min-[700px]:p-11">
				<div className="mb-2 text-xs font-extrabold tracking-[2px] text-accent uppercase">
					Anime được xem nhiều nhất
				</div>
				<h1 className="text-[clamp(31px,4vw,54px)] leading-[1.05] font-extrabold tracking-[-1.5px]">
					{anime.title}
				</h1>
				{anime.originalTitle && (
					<p className="mt-1 text-sm text-muted">{anime.originalTitle}</p>
				)}

				<div className="mt-3 flex flex-wrap items-center gap-3 text-[13px] text-[#c3c4d4]">
					{metaItems.map((item, index) =>
						index === 0 ? (
							<span
								key={item}
								className="rounded-[5px] bg-accent/20 px-2 py-0.5 text-[#ffd4e8]"
							>
								{item}
							</span>
						) : (
							<span key={`${item}-${index}`}>{item}</span>
						),
					)}
				</div>

				<p className="my-4 line-clamp-3 max-w-[460px] text-[#c6c4d4]">
					{synopsis}
				</p>

				<div className="flex flex-wrap gap-2">
					<Link
						to={`/xem/${anime.slug}`}
						className="rounded-lg bg-linear-to-r from-accent to-accent-soft px-4.5 py-2.5 font-bold shadow-accent transition duration-200 hover:-translate-y-0.5 active:scale-[0.97]"
					>
						▶ Xem ngay
					</Link>
					<button
						type="button"
						onClick={() => toggle('watchlist', anime)}
						className="rounded-lg bg-white/10 px-4.5 py-2.5 font-bold transition duration-200 hover:-translate-y-0.5 active:scale-[0.97]"
					>
						{inWatchlist ? '✓ Đã lưu' : '＋ Danh sách'}
					</button>
					<Link
						to={`/phim/${anime.slug}`}
						className="rounded-lg px-4.5 py-2.5 font-bold text-muted transition hover:text-ink"
					>
						Chi tiết →
					</Link>
				</div>
			</div>
		</section>
	);
}
